
import Vue from "vue";
import { analytics } from "@/setups/firebase";
import { logEvent } from "firebase/analytics";
import { Context } from "@nuxt/types";
import { DocumentErrorCodes } from "@ambii-com/core";
import { Clinic, FormConfig, Form, DocumentError } from "@ambii-com/core";
import { fsLocations } from "@/dao/FsLocations";
import { mapMutations } from "vuex";
import { isFirestoreError } from "@/utils/typeChecks";
import TermsOfServices from "~/pages/appointment/termsOfServices.vue";
import ReportBugDialog from "@/components/pages/form/reportBugDialog.vue";

export default Vue.extend({
  middleware: ["cleanExpiredFormBackups"],
  components: { TermsOfServices, ReportBugDialog },
  async asyncData({ env, app, params, error, $sentry, store}: Context) {
    try {
      const clinic: Clinic = await fsLocations
        .getClinicRef(params.clinicId)
        .get()
        .then(Clinic.fromDoc);
      const forms: Array<Form> = await fsLocations
        .getFormRefByAvailabilityAndLocale(clinic.id, true, app.i18n.locale)
        .get()
        .then(({ docs }) => Form.fromDocs(docs));
      const formConfig: FormConfig | null = clinic.formConfigId
        ? await fsLocations
            .getFormConfigRef(clinic.formConfigId)
            .get()
            .then(FormConfig.fromDoc)
        : null;
      store.dispatch("auth/setClinic", clinic);
      return { clinic, forms, formConfig };
    } catch (e) {
      if (e instanceof DocumentError) {
        switch (e.code) {
          case DocumentErrorCodes.DOC_NOT_FOUND:
          case DocumentErrorCodes.DOC_MISSING_DATA:
            return error({ statusCode: 404 });
        }
      } else if (isFirestoreError(e)) {
        switch (e.code) {
          case "unavailable":
            return error({
              statusCode: 400,
              custom: true,
              messageI18nKey: "error.message.unavailable400",
              titleI18nKey: "error.title.unavailable400",
            });
          case "deadline-exceeded":
            return error({ statusCode: 408 });
        }
      }
      $sentry.captureException(e);
      return error({ statusCode: 500 });
    }
  },
  data(): {
    backupStoreDialog: {
      show: Boolean;
      form: Form | null;
    };
    isNavigating: Boolean;
    networkError: Boolean;
    termsOfServiceSheet: Boolean;
    clinic: Clinic | null;
    forms: Form[];
    formConfig: FormConfig | null;
  } {
    return {
      backupStoreDialog: {
        show: false,
        form: null,
      },
      isNavigating: false,
      networkError: false,
      termsOfServiceSheet: false,
      clinic: null,
      forms: [],
      formConfig: null,
    };
  },
  computed: {
    startTitle(): string {
      // Use default if no config or startTitle is blank
      return this.formConfig && this.formConfig?.startTitle
        ? this.formConfig.startTitle
        : `${this.clinic.name} ${this.$t("form.index.medicalQuestionnaire")}`;
    },
    startSubtitle(): string {
      return this.formConfig && this.formConfig?.startSubtitle
        ? this.formConfig.startSubtitle
        : this.$t("form.index.defaultSubtitle");
    },
    currentYear(): number {
      return new Date().getFullYear();
    },
  },
  methods: {
    ...mapMutations("form", ["resetFormState"]),
    loadForm(form: Form) {
      this.isNavigating = true;
      this.resetFormState();
      logEvent(analytics, "form_entry", {
        form_id: form.id,
        form_title: form.title,
        form_locale: form.locale,
      });
      this.$nuxt.$router.push(
        this.localePath(`/form/${form.id}/${form.firstPageId}`)
      );
    },
  },
  async mounted() {
    document.title = await this.clinic ? `${this.clinic.name} WEB問診 - Ambii` : "Ambii WEB問診";
  },
  created() {
    window.addEventListener("offline", () => {
      this.networkError = true;
    });
    window.addEventListener("online", () => {
      this.networkError = false;
    });
  },
});
